import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { VenueMapMarker } from '../../../../../models/big-screen-dashboard.model';

/** A marker with its live entries/exits/re-entries figures resolved for display. */
interface MarkerView {
  marker: VenueMapMarker;
  entries: number;
  exits: number;
  reentries: number;
}

/** The image box's pixel size, or null before an image has loaded. */
interface BoxSize {
  width: number;
  height: number;
}

/**
 * Renders a `venueMap` widget: an uploaded floor-plan image with an entrance
 * marker pinned over each configured spot, showing that entrance's live entries,
 * exits and re-entries. Marker positions are percentages of the image box so they
 * stay put as the widget is resized.
 *
 * The image box is sized in JavaScript to the largest rectangle with the image's
 * aspect ratio that fits inside the widget (a "contain" fit). CSS alone can't do
 * this letterbox-free — fixing one axis to 100% and clamping the other with
 * `max-*` distorts the ratio in the cross case — and the box must hug the image
 * exactly so percentage-based markers never drift into empty space. It's
 * recomputed on widget resize via a {@link ResizeObserver}.
 *
 * In edit mode a marker can be dragged directly on the image; the new position is
 * emitted on drop for the parent to persist. The component never mutates its
 * `markers` input in place — it emits a new array so change detection is clean.
 */
@Component({
  selector: 'app-venue-map-widget',
  standalone: false,
  templateUrl: './venue-map-widget.component.html',
  styleUrls: ['./venue-map-widget.component.scss'],
})
export class VenueMapWidgetComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() imageData?: string;
  @Input() markers: VenueMapMarker[] = [];
  @Input() attendanceSummary: any = {};
  /** Enables inline marker dragging; false on the live wall display. */
  @Input() editing: boolean = false;
  @Input() dark: boolean = false;

  /** Emits the full marker list whenever a marker is moved, for persistence. */
  @Output() markersChange = new EventEmitter<VenueMapMarker[]>();

  @ViewChild('imageBox') imageBox?: ElementRef<HTMLElement>;

  /** The image box's fitted pixel size, bound to the box; null until it fits. */
  boxSize: BoxSize | null = null;

  /** The loaded image's natural width/height ratio, used to fit the box. */
  private imageRatio: number | null = null;
  /** Watches the widget's own box so the image refits when it's resized. */
  private resizeObserver?: ResizeObserver;

  /** Index of the marker being dragged, or null when idle. */
  private dragIndex: number | null = null;
  /** Live position of the dragged marker, applied before the drop commits. */
  private liveMarker: VenueMapMarker | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // A replacement image may have a different aspect ratio; drop the fitted size
    // until the new image's `load` reports its dimensions.
    if (changes['imageData']) {
      this.imageRatio = null;
      this.boxSize = null;
    }
  }

  ngAfterViewInit(): void {
    this.observeHostResize();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /** Captures the loaded image's aspect ratio, then fits the box to the widget. */
  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      this.imageRatio = img.naturalWidth / img.naturalHeight;
      this.fitBox();
    }
  }

  /** Markers paired with their live figures, in the input's order. */
  get markerViews(): MarkerView[] {
    const entrances = this.attendanceSummary?.entrances || {};
    return this.markers.map((marker, index) => {
      // Show the live drag position for the marker under the pointer.
      const active = index === this.dragIndex && this.liveMarker ? this.liveMarker : marker;
      const data = entrances[marker.entranceName] || {};
      return {
        marker: active,
        entries: Number(data.entries || 0),
        exits: Number(data.exits || 0),
        reentries: Number(data.reentries || 0),
      };
    });
  }

  onMarkerPointerDown(event: PointerEvent, index: number): void {
    if (!this.editing) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragIndex = index;
    this.liveMarker = { ...this.markers[index] };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (this.dragIndex === null || !this.liveMarker) return;
    const rect = this.imageBox?.nativeElement.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    // Convert the pointer's position within the image box to a 0–100 percentage
    // and clamp so a marker can't be dragged off the floor plan.
    const x = this.clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = this.clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
    this.liveMarker = { ...this.liveMarker, x, y };
  }

  onPointerUp(event: PointerEvent): void {
    if (this.dragIndex === null || !this.liveMarker) return;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    const committed = this.markers.map((marker, index) =>
      index === this.dragIndex ? { ...this.liveMarker! } : marker
    );
    this.dragIndex = null;
    this.liveMarker = null;
    this.markersChange.emit(committed);
  }

  trackByMarker(index: number, view: MarkerView): string {
    return `${index}:${view.marker.entranceName}`;
  }

  /**
   * Watches the component's own box so the image refits whenever the widget is
   * resized. The observer callback runs outside Angular and only re-enters to
   * update the bound box size when it actually changes, keeping resize cheap.
   */
  private observeHostResize(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.fitBox());
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  /**
   * Sizes the image box to the largest rectangle with the image's aspect ratio
   * that fits inside the widget, so the whole floor plan is always visible and
   * the box hugs it with no letterboxing. No-ops until both the widget size and
   * the image ratio are known.
   */
  private fitBox(): void {
    const ratio = this.imageRatio;
    if (!ratio) return;
    const el = this.host.nativeElement;
    const availW = el.clientWidth;
    const availH = el.clientHeight;
    if (availW <= 0 || availH <= 0) return;

    // Width-limited when the widget is wider than the image ratio; else height-limited.
    const width = availW / availH > ratio ? availH * ratio : availW;
    const height = width / ratio;
    const next: BoxSize = { width: Math.floor(width), height: Math.floor(height) };
    if (this.boxSize?.width === next.width && this.boxSize?.height === next.height) return;

    // Re-enter Angular so the template's [style] bindings pick up the new size.
    this.zone.run(() => (this.boxSize = next));
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
